'use client';

import { getClientApplicationConfig } from '@hc-frontend/core-context';
import type { Placement, ProductType } from '@hc-frontend/core-types';
import { Tags } from '@hc-frontend/core-types';
import type {
  SelectButtonBaseProps,
  SelectButtonContainerProps,
  SelectButtonOptionProps,
  SubmitFormEvent,
} from '@hc-frontend/core-ui';
import {
  AutoSubmitRadioButton,
  ErrorMessage,
  useEventBus,
  useJourney,
  useTrackingContexts,
} from '@hc-frontend/core-ui';
import { addClassName } from '@hc-frontend/core-utils-format';
import type { WhenYouNeedMedicareCoverage } from '@hc-frontend/features-leads';
import { Question } from '@hc-frontend/features-leads';
import { SelectButtonWithTracking } from '@hc-frontend/shells-shared';
import type { FormElement } from '@hc-frontend/third-party-snowplow';
import type React from 'react';
import type {
  ChangeEvent,
  ChangeEventHandler,
  FormEventHandler,
  MouseEventHandler,
} from 'react';
import { useActionState, useRef, useState } from 'react';

import { SurveyPlateTransitionOptions } from '../../../organisms/survey-plate-transition-options/survey-plate-transition-options';
import { INPUT_NAME, plateName } from './constants';
import { actionForm } from './form-action-server';

const iconOptionMap: Record<WhenYouNeedMedicareCoverage, string> = {
  RIGHT_AWAY: 'icon-stopwatch.png',
  NEXT_MONTH_OR_LATER: 'icon-calendar.png',
  NOT_SURE: 'icon-question.png',
};

function Option({
  label,
  value,
  onChange,
  onClick,
  inputId,
  disabled,
  name,
  required,
  inputProps,
  type,
}: SelectButtonOptionProps) {
  const config = getClientApplicationConfig();
  const { marketplaceStaticBaseUrl } = config.assets;

  const iconSource = `${marketplaceStaticBaseUrl}/marketplace/images/${iconOptionMap[value as WhenYouNeedMedicareCoverage]}`;

  return (
    <label
      className="hc-cluster-layout group relative w-full cursor-pointer flex-col flex-nowrap justify-center gap-16-16 rounded-lg border border-primary-900 p-16-24 outline-offset-2 outline-tertiary-900 transition-all hover:border-secondary-variant-900 hover:bg-secondary-variant-50 active:scale-[0.98] has-[:disabled]:pointer-events-none has-[:checked]:border-secondary-variant-900 has-[:checked]:bg-secondary-variant-50 has-[:focus]:outline"
      htmlFor={`${inputId}-${value}`}
    >
      <AutoSubmitRadioButton
        {...inputProps}
        id={`${inputId}-${value}`}
        className="peer absolute opacity-0"
        onChange={onChange}
        onClick={onClick}
        name={name}
        value={value}
        disabled={disabled}
        required={required}
        type={type}
      />
      <img src={iconSource} alt={value} width="50" height="50" />
      <span className="hc-typography--paragraph-lg text-center font-semibold leading-tight text-primary-900">
        {label}
      </span>
      <span
        className="hidden size-[20px] min-w-[20px] items-center justify-center rounded-full border-2 border-primary-900 transition-all after:block after:size-[12px] after:rounded-full after:bg-gray-50 after:transition-all after:content-[''] group-hover:border-secondary-variant-900 after:group-hover:bg-secondary-variant-50 peer-checked:border-secondary-variant-900 after:peer-checked:bg-secondary-900 tablet:flex"
        aria-hidden="true"
      />
    </label>
  );
}

function OptionsContainer({ children, errorText }: SelectButtonContainerProps) {
  return (
    <div className="flex w-full flex-col items-center gap-8-8">
      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-8-8">
        {children}
      </div>
      <span className="hc-error-text">{errorText}</span>
    </div>
  );
}

export function CoverageUrgency({
  required,
  children,
  options,
  placement,
  plateIndex,
  product,
  showSkipButton,
}: {
  required: boolean;
  showSkipButton?: boolean;
  children: React.ReactNode;
  options: SelectButtonBaseProps['options'];
  product: ProductType;
  plateIndex: number;
  placement: Placement;
}) {
  const { journeyId } = useJourney();
  const metricTags: Tags = {
    plate_name: plateName,
    plate_index: plateIndex,
    journey_id: journeyId,
    product,
    placement,
  };

  const [currentOptions, setCurrentOptions] = useState(options);
  const [state, formSubmitHandler, isPending] = useActionState(actionForm, {
    _errors: [],
  });

  const optionsRef = useRef(options);

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    const newOptions = options.map((option) => ({
      ...option,
      checked: option.value === value,
    }));
    optionsRef.current = newOptions;
    setCurrentOptions(newOptions);
  };

  const handleSkipClick: MouseEventHandler<HTMLButtonElement> = () => {
    const clearedOptions = options.map((option) => ({
      ...option,
      checked: false,
    }));
    setCurrentOptions(clearedOptions);
  };

  const buildFormElements = () => {
    const checkedOption = optionsRef.current.find((option) => option.checked);
    const value = checkedOption?.value ?? null;

    const element: FormElement = {
      name: Question.Enum.WHEN_YOU_NEED_MEDICARE_COVERAGE,
      value,
      nodeName: 'INPUT',
    };
    return [element];
  };

  const eventBus = useEventBus();
  const contexts = useTrackingContexts();

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    const formElements = buildFormElements();

    eventBus.publish<SubmitFormEvent, (EventTarget & HTMLFormElement) | null>({
      id: plateName,
      topic: 'core.ui.form.submit',
      source: event.currentTarget,
      submitForm: {
        id: plateName,
        elements: formElements,
      },
      contexts,
    });
  };

  const isSubmitButtonDisabled = (): boolean => {
    const isAnyOptionSelected = currentOptions.some((option) => option.checked);
    return required && !isAnyOptionSelected;
  };

  return (
    <form
      action={formSubmitHandler}
      className={addClassName('w-full')}
      onSubmit={handleSubmit}
    >
      <div className="my-16-16 w-full">
        <SelectButtonWithTracking
          data-testid="survey-coverageUrgency-options"
          isPending={isPending}
          name={INPUT_NAME}
          onChange={handleOnChange}
          error={false}
          errorText={<ErrorMessage>{state[INPUT_NAME]?._errors}</ErrorMessage>}
          id={INPUT_NAME}
          required={required}
          options={currentOptions}
          OptionView={Option}
          ContainerView={OptionsContainer}
          metricTags={metricTags}
        />
      </div>

      <SurveyPlateTransitionOptions
        testIdPrefix="survey-coverageUrgency"
        isPending={isPending}
        continueButtonDisabled={isSubmitButtonDisabled()}
        metricTags={metricTags}
        handleSkipClick={handleSkipClick}
        showSkipButton={showSkipButton}
      />
      {children}
    </form>
  );
}
